"use client"
import { useState, useEffect } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X, AlertTriangle, User } from 'lucide-react'
import Button from '@/components/ui/Button'

interface User {
  id: string
  name: string
  email: string
  role: 'Admin' | 'Editor' | 'Author'
}

interface UserDeleteModalProps {
  isOpen: boolean
  onClose: () => void
  user: User | null
  onConfirmDelete: (userId: string) => Promise<void>
}

export default function UserDeleteModal({ isOpen, onClose, user, onConfirmDelete }: UserDeleteModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const handleDelete = async () => {
    // Additional validation
    if (!user || !user.id || !user.name) {
      setError('Invalid user data. Cannot proceed with deletion.')
      return
    }

    // Prevent double submission
    if (isLoading) {
      return
    }

    setIsLoading(true)
    setError('')

    try {
      await onConfirmDelete(user.id)
      onClose()
    } catch (err) {
      console.error('Error deleting user:', err)

      let errorMessage = 'Failed to delete user'
      if (err instanceof TypeError && err.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (err instanceof Error) {
        // Handle specific error cases
        if (err.message.includes('404')) {
          errorMessage = 'User not found. It may have already been deleted.'
        } else if (err.message.includes('403')) {
          errorMessage = 'You do not have permission to delete this user.'
        } else if (err.message.includes('500')) {
          errorMessage = 'Server error. Please try again later.'
        } else {
          errorMessage = err.message
        }
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setError('')
      onClose()
    }
  }

  // Enhanced close handler for better UX
  const handleModalClose = (open: boolean) => {
    if (!open && !isLoading) {
      setError('')
      onClose()
    }
  }

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      // Cleanup any pending operations
      setError('')
      setIsLoading(false)
    }
  }, [])

  // Reset error when modal closes
  useEffect(() => {
    if (!isOpen) {
       
      setError('')
    }
  }, [isOpen])

  // Early return if user data is invalid
  if (!user || !user.id || !user.name || !user.email) {
    console.warn('UserDeleteModal: Invalid user data provided', user)
    return null
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleModalClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100]" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg shadow-xl z-[101]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-user-title"
          aria-describedby="delete-user-description"
        >
          <div className={`p-6 ${isLoading ? 'pointer-events-none opacity-75' : ''}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <Dialog.Title id="delete-user-title" className="text-lg font-semibold text-gray-900">
                Delete User
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  disabled={isLoading}
                  aria-label="Close delete user dialog"
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>

            {/* Warning Icon and Message */}
            <div className="flex items-start space-x-3 mb-6">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-10 w-10 text-red-500" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <h3 id="delete-user-description" className="text-lg font-medium text-gray-900 mb-2">
                  Delete User Account
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Are you sure you want to delete this user account? This action cannot be undone and all associated data will be permanently removed.
                </p>

                {/* User Info */}
                <div className="bg-gray-50 rounded-lg p-4" role="region" aria-label="User information to be deleted">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center" aria-hidden="true">
                      <User className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900" aria-label={`User name: ${user.name}`}>{user.name}</div>
                      <div className="text-sm text-gray-500" aria-label={`Email: ${user.email}`}>{user.email}</div>
                      <div className="text-xs text-gray-400 capitalize" aria-label={`Role: ${user.role}`}>{user.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md" role="alert" aria-live="assertive">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
                  <p className="ml-2 text-sm text-red-700">{error}</p>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <p className="ml-2 text-sm text-blue-700">Deleting user...</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3" role="group" aria-label="Delete user actions">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isLoading}
                aria-label="Cancel user deletion"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleDelete}
                disabled={isLoading}
                aria-label={`Delete user ${user.name}`}
                aria-describedby="delete-user-description"
              >
                {isLoading ? 'Deleting...' : 'Delete User'}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
